/**
 * RELEASE INFO SECTION
 *
 * Shared release-type + conditional-fields UI, extracted from
 * AddPolishModal so all 8 item types (polish, plate, tool, powder,
 * embellishment, decal, acrylic, cuticle_care) can present the same
 * pattern.
 *
 * The releaseType dropdown drives what conditional inputs render:
 *   - collection → collectionName (with optional brand-collection
 *     datalist autocomplete)
 *   - event → eventName + eventTheme
 *   - box → boxName + boxTheme
 *   - groupCustom → groupName + groupTheme
 *   - exclusive → exclusiveTo + exclusiveTheme
 *   - charity → charityName + charityCause
 *   - convention → conventionName
 *
 * Below the conditional block, three always-visible fields render:
 * releaseYear, releaseMonth, releaseSeason.
 *
 * All fields are strings so the caller can trim on save without
 * juggling null/undefined variants. The consumer decides which fields
 * to persist based on the current releaseType.
 */


export interface ReleaseInfoValue {
  releaseType: string;
  collectionName: string;
  eventName: string;
  eventTheme: string;
  boxName: string;
  boxTheme: string;
  groupName: string;
  groupTheme: string;
  exclusiveTo: string;
  exclusiveTheme: string;
  charityName: string;
  charityCause: string;
  conventionName: string;
  releaseYear: string;
  releaseMonth: string;
  releaseSeason: string;
}

/** Seed a ReleaseInfoValue from an item document (polish, plate,
 *  decal, etc.). Handles legacy field aliases from before the Jan 2026
 *  schema refactor (eventSource → eventName, collection → collectionName,
 *  box → boxName, event → eventName, collab → collabWith). Use this
 *  in DetailModals' initial state and reset-on-cancel handlers. */
export function seedReleaseInfoFromItem(item: any): ReleaseInfoValue {
  return {
    releaseType: item?.releaseType || 'unknown',
    collectionName: item?.collectionName || item?.collection || '',
    eventName: item?.eventName || item?.eventSource || item?.event || '',
    eventTheme: item?.eventTheme || '',
    boxName: item?.boxName || item?.box || '',
    boxTheme: item?.boxTheme || '',
    groupName: item?.groupName || '',
    groupTheme: item?.groupTheme || '',
    exclusiveTo: item?.exclusiveTo || '',
    exclusiveTheme: item?.exclusiveTheme || '',
    charityName: item?.charityName || '',
    charityCause: item?.charityCause || '',
    conventionName: item?.conventionName || '',
    releaseYear: item?.releaseYear != null ? String(item.releaseYear) : '',
    releaseMonth: item?.releaseMonth || '',
    releaseSeason: item?.releaseSeason || '',
  };
}

/** Zero-state initializer — useful for `useState<ReleaseInfoValue>(emptyReleaseInfo())`. */
export function emptyReleaseInfo(): ReleaseInfoValue {
  return {
    releaseType: 'unknown',
    collectionName: '',
    eventName: '',
    eventTheme: '',
    boxName: '',
    boxTheme: '',
    groupName: '',
    groupTheme: '',
    exclusiveTo: '',
    exclusiveTheme: '',
    charityName: '',
    charityCause: '',
    conventionName: '',
    releaseYear: '',
    releaseMonth: '',
    releaseSeason: '',
  };
}

/** All the category-specific release fields. Save-time we set every
 *  one — either to the user's value (when it belongs to the current
 *  releaseType) or to null (to clear stale legacy data from a
 *  different type). This keeps switching release types from leaving
 *  ghost fields on the item. */
const CATEGORY_FIELDS = [
  'collectionName',
  'eventName',
  'eventSource', // legacy — always null out; canonical field is eventName
  'eventTheme',
  'boxName',
  'boxTheme',
  'groupName',
  'groupTheme',
  'exclusiveTo',
  'exclusiveTheme',
  'charityName',
  'charityCause',
  'conventionName',
] as const;

/** Build a save-time object. Fields relevant to the current
 *  releaseType are written to their trimmed values (or `null` if the
 *  user cleared them); fields for OTHER release types are explicitly
 *  set to `null` so switching from e.g. 'unknown' to 'box' doesn't
 *  leave a stale collectionName on the item. Callers spread this
 *  into their Firestore write payload. */
export function releaseInfoSaveShape(v: ReleaseInfoValue): Record<string, unknown> {
  const out: Record<string, unknown> = {
    releaseType: v.releaseType || 'unknown',
  };

  const set = (key: string, value: string) => {
    const trimmed = value.trim();
    out[key] = trimmed || null;
  };

  // For KNOWN release types, null every category field then set the
  // ones for the current type — this keeps switching types clean
  // (e.g. 'unknown' with legacy collectionName → 'box' clears it).
  // For 'unknown' / 'other', preserve existing fields untouched — the
  // user hasn't committed to a category yet, so nothing to reclassify.
  const isKnownType = ![
    '',
    'unknown',
    'other',
  ].includes(v.releaseType);

  if (isKnownType) {
    for (const key of CATEGORY_FIELDS) out[key] = null;
  }

  if (v.releaseType === 'collection') set('collectionName', v.collectionName);
  if (v.releaseType === 'event') {
    set('eventName', v.eventName);
    set('eventTheme', v.eventTheme);
  }
  if (v.releaseType === 'box') {
    set('boxName', v.boxName);
    set('boxTheme', v.boxTheme);
  }
  if (v.releaseType === 'groupCustom') {
    set('groupName', v.groupName);
    set('groupTheme', v.groupTheme);
  }
  if (v.releaseType === 'exclusive') {
    set('exclusiveTo', v.exclusiveTo);
    set('exclusiveTheme', v.exclusiveTheme);
  }
  if (v.releaseType === 'charity') {
    set('charityName', v.charityName);
    set('charityCause', v.charityCause);
  }
  if (v.releaseType === 'convention') set('conventionName', v.conventionName);

  // Season/year/month always persist (not tied to releaseType).
  if (v.releaseYear.trim()) out.releaseYear = v.releaseYear.trim();
  if (v.releaseMonth.trim()) out.releaseMonth = v.releaseMonth.trim();
  if (v.releaseSeason.trim()) out.releaseSeason = v.releaseSeason.trim();
  return out;
}

const RELEASE_TYPE_OPTIONS: { value: string; label: string; emoji: string }[] = [
  { value: 'unknown', label: 'Unknown', emoji: '❓' },
  { value: 'core', label: 'Core / Permanent', emoji: '🏠' },
  { value: 'collection', label: 'Collection', emoji: '📚' },
  { value: 'event', label: 'Event', emoji: '🎪' },
  { value: 'box', label: 'Box', emoji: '📦' },
  { value: 'groupCustom', label: 'Group Custom', emoji: '👥' },
  { value: 'exclusive', label: 'Exclusive', emoji: '⭐' },
  { value: 'charity', label: 'Charity', emoji: '💝' },
  { value: 'limitedEdition', label: 'Limited Edition', emoji: '✨' },
  { value: 'convention', label: 'Convention', emoji: '🎫' },
  { value: 'other', label: 'Other', emoji: '📝' },
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const SEASONS = ['Spring', 'Summer', 'Fall', 'Winter'];

interface Props {
  value: ReleaseInfoValue;
  onChange: (next: ReleaseInfoValue) => void;
  /** Optional list of the brand's official collections. When provided,
   *  the Collection Name input uses a datalist so collectors get
   *  suggestions as they type. */
  brandCollectionSuggestions?: string[];
  /** Brand name for the suggestions hint line. */
  brandName?: string;
  /** id-collision guard — non-polish modals use different id namespaces
   *  so multiple ReleaseInfoSection instances on one page don't share a
   *  datalist. Defaults to a generic value. */
  datalistId?: string;
}

export default function ReleaseInfoSection({
  value,
  onChange,
  brandCollectionSuggestions = [],
  brandName,
  datalistId = 'brand-collection-suggestions',
}: Props) {
  const patch = (partial: Partial<ReleaseInfoValue>) => {
    onChange({ ...value, ...partial });
  };

  const renderConditional = () => {
    switch (value.releaseType) {
      case 'collection':
        return (
          <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg space-y-3">
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">
                📚 Collection Name
              </label>
              <input
                type="text"
                value={value.collectionName}
                onChange={(e) => patch({ collectionName: e.target.value })}
                className="input-light font-body"
                placeholder="e.g., Enchanted Forest, Winter Wonderland"
                list={brandCollectionSuggestions.length > 0 ? datalistId : undefined}
              />
              {brandCollectionSuggestions.length > 0 && (
                <>
                  <datalist id={datalistId}>
                    {brandCollectionSuggestions.map((n) => (
                      <option key={n} value={n} />
                    ))}
                  </datalist>
                  <div className="text-[10px] text-purple-600 mt-1 font-body">
                    Suggestions from {brandName ? `${brandName}'s` : 'the brand\'s'} official collections.
                  </div>
                </>
              )}
            </div>
          </div>
        );
      case 'event':
        return (
          <div className="mt-3 p-3 bg-pink-50 border border-pink-200 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-pink-700 mb-1">🎪 Event Name</label>
                <input type="text" value={value.eventName} onChange={(e) => patch({ eventName: e.target.value })} className="input-light font-body" placeholder="e.g., Polish Pickup, HHC" />
              </div>
              <div>
                <label className="block text-sm font-medium text-pink-700 mb-1">🎨 Event Theme</label>
                <input type="text" value={value.eventTheme} onChange={(e) => patch({ eventTheme: e.target.value })} className="input-light font-body" placeholder="e.g., Periodic Table, Autumn Harvest" />
              </div>
            </div>
          </div>
        );
      case 'box':
        return (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-amber-700 mb-1">📦 Box Name</label>
                <input type="text" value={value.boxName} onChange={(e) => patch({ boxName: e.target.value })} className="input-light font-body" placeholder="e.g., Polished Gamers Box" />
              </div>
              <div>
                <label className="block text-sm font-medium text-amber-700 mb-1">🎨 Box Theme</label>
                <input type="text" value={value.boxTheme} onChange={(e) => patch({ boxTheme: e.target.value })} className="input-light font-body" placeholder="e.g., October 2024" />
              </div>
            </div>
          </div>
        );
      case 'groupCustom':
        return (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-blue-700 mb-1">👥 Group Name</label>
                <input type="text" value={value.groupName} onChange={(e) => patch({ groupName: e.target.value })} className="input-light font-body" placeholder="e.g., Reddit Lacqueristas" />
              </div>
              <div>
                <label className="block text-sm font-medium text-blue-700 mb-1">🎨 Theme <span className="font-normal text-blue-500">(optional)</span></label>
                <input type="text" value={value.groupTheme} onChange={(e) => patch({ groupTheme: e.target.value })} className="input-light font-body" placeholder="e.g., Anniversary Custom" />
              </div>
            </div>
          </div>
        );
      case 'exclusive':
        return (
          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg space-y-3">
            <div>
              <label className="block text-sm font-medium text-yellow-700 mb-1">⭐ Exclusive To</label>
              <input type="text" value={value.exclusiveTo} onChange={(e) => patch({ exclusiveTo: e.target.value })} className="input-light font-body" placeholder="e.g., Color4Nails, Holo Taco website" />
            </div>
            <div>
              <label className="block text-sm font-medium text-yellow-700 mb-1">🏷️ Exclusive Theme <span className="font-normal text-yellow-500">(optional)</span></label>
              <input type="text" value={value.exclusiveTheme} onChange={(e) => patch({ exclusiveTheme: e.target.value })} className="input-light font-body" placeholder="e.g., Black Friday 2024" />
            </div>
          </div>
        );
      case 'charity':
        return (
          <div className="mt-3 p-3 bg-rose-50 border border-rose-200 rounded-lg space-y-3">
            <div>
              <label className="block text-sm font-medium text-rose-700 mb-1">💝 Charity Name</label>
              <input type="text" value={value.charityName} onChange={(e) => patch({ charityName: e.target.value })} className="input-light font-body" placeholder="e.g., Autism Speaks, Red Cross" />
            </div>
            <div>
              <label className="block text-sm font-medium text-rose-700 mb-1">🎗️ Cause <span className="font-normal text-rose-500">(optional)</span></label>
              <input type="text" value={value.charityCause} onChange={(e) => patch({ charityCause: e.target.value })} className="input-light font-body" placeholder="e.g., Autism Awareness" />
            </div>
          </div>
        );
      case 'convention':
        return (
          <div className="mt-3 p-3 bg-teal-50 border border-teal-200 rounded-lg">
            <label className="block text-sm font-medium text-teal-700 mb-1">🎫 Convention Name</label>
            <input type="text" value={value.conventionName} onChange={(e) => patch({ conventionName: e.target.value })} className="input-light font-body" placeholder="e.g., Polish Con, Indie Expo" />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-light-secondary mb-1">
          Release Type
        </label>
        <select
          value={value.releaseType || 'unknown'}
          onChange={(e) => patch({ releaseType: e.target.value })}
          className="select-light font-body"
        >
          {RELEASE_TYPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.emoji} {option.label}
            </option>
          ))}
        </select>
      </div>

      {renderConditional()}

      <div className="grid grid-cols-3 gap-4 mt-2">
        <div>
          <label className="block text-sm font-medium text-light-secondary mb-1">Year</label>
          <input type="number" value={value.releaseYear} onChange={(e) => patch({ releaseYear: e.target.value })} className="input-light font-body" placeholder="e.g., 2024" />
        </div>
        <div>
          <label className="block text-sm font-medium text-light-secondary mb-1">Month</label>
          <select value={value.releaseMonth} onChange={(e) => patch({ releaseMonth: e.target.value })} className="select-light font-body">
            <option value="">—</option>
            {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-light-secondary mb-1">Season</label>
          <select value={value.releaseSeason} onChange={(e) => patch({ releaseSeason: e.target.value })} className="select-light font-body">
            <option value="">—</option>
            {SEASONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}
